import { Loader2, Lock, Settings2, ShieldAlert, Users2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/Dialog';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { api, type RoomAccess } from '@/lib/api';
import { useAuth } from '@/features/auth/AuthContext';

interface Props {
  roomId: string;
  roomName: string;
  onAccessChanged?: (access: RoomAccess) => void;
}

const visibilityCopy: Record<
  RoomAccess['visibility'],
  { label: string; icon: typeof Users2; description: string }
> = {
  public: {
    label: 'Public',
    icon: Users2,
    description: 'Anyone with the URL or QR code can join. Best for customer-facing demos.',
  },
  authenticated: {
    label: 'Signed-in only',
    icon: Lock,
    description: 'Only Cisco team members with a cAspeR account can join.',
  },
  admin: {
    label: 'Admins only',
    icon: ShieldAlert,
    description: 'Only cAspeR admins can join. Use for pre-event setup.',
  },
};

export function RoomSettingsDialog({ roomId, roomName, onAccessChanged }: Props) {
  const { user } = useAuth();
  const canManage = user?.role === 'admin' || user?.role === 'editor';

  const [open, setOpen] = useState(false);
  const [access, setAccess] = useState<RoomAccess | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.getRoomAccess(roomId)
      .then((a) => {
        setAccess(a);
        onAccessChanged?.(a);
      })
      .catch((err) =>
        toast.error('Failed to load room settings', {
          description: err instanceof Error ? err.message : String(err),
        }),
      )
      .finally(() => setLoading(false));
  }, [open, roomId, onAccessChanged]);

  async function changeVisibility(next: RoomAccess['visibility']) {
    setSaving(true);
    try {
      const row = await api.setRoomVisibility(roomId, next);
      setAccess(row);
      onAccessChanged?.(row);
      toast.success(`Visibility set to ${visibilityCopy[next].label}`);
    } catch (err) {
      toast.error('Update failed', {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Settings2 className="h-4 w-4" /> Room settings
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="h-4 w-4" /> {roomName}
          </DialogTitle>
          <DialogDescription>
            {canManage
              ? 'Control who can join this room.'
              : 'You have view-only access.'}
          </DialogDescription>
        </DialogHeader>

        {loading || !access ? (
          <div className="grid h-40 place-items-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-2">
            <Label>Who can join</Label>
            <Select
              value={access.visibility}
              onValueChange={(v) => changeVisibility(v as RoomAccess['visibility'])}
              disabled={!canManage || saving || access.closed}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(visibilityCopy) as RoomAccess['visibility'][]).map((v) => {
                  const Icon = visibilityCopy[v].icon;
                  return (
                    <SelectItem key={v} value={v}>
                      <span className="flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        {visibilityCopy[v].label}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {access.closed
                ? 'This session is closed and the URL is retired.'
                : visibilityCopy[access.visibility].description}
            </p>
          </div>
        )}

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" size="sm">Done</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
